import styled from 'styled-components';

import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useForm, FormProvider } from 'react-hook-form';

import { PAGES } from 'constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { PageTitle } from 'components/PageTitle';
import { DetailFormValue } from 'components/DetailsView';
import { DetailFormSelect } from 'components/DetailsView/DetailFormSelect';
import { Loader, LoaderModeType } from 'components/Loader';
import { useCreateToteamUserMutation, InternalUserRole, TotadminPermission } from 'data/__generated__';

const ROLE_OPTIONS = [
    { value: InternalUserRole.Toteam, label: 'Administrateur (Toteam)' },
    { value: InternalUserRole.Deliverer, label: 'Livreur (Totops)' },
    { value: InternalUserRole.Preparer, label: 'Préparateur (Totops)' },
];

type FormData = {
    emailAddress: string;
    firstname: string;
    lastname: string;
    role: InternalUserRole;
};

export const CreateToteamUser = () => {
    const methods = useForm<FormData>({
        defaultValues: {
            role: InternalUserRole.Toteam,
        },
    });
    const navigate = useNavigate();
    const [createToteamUser, { loading }] = useCreateToteamUserMutation({
        onError: (error) => toast.error(error.message),
    });
    const {
        handleSubmit,
        register,
        formState: { isDirty },
    } = methods;

    const onSubmit = handleSubmit(async (fields) => {
        const { emailAddress, firstname, lastname, role } = fields;

        if (emailAddress.includes('+admin@totem.co')) {
            toast.error(
                'Attention - ne pas utiliser d\'adresse "+admin@totem.co" mais uniquement ajouter le role global "Toteam"',
                {
                    autoClose: false,
                },
            );
            return;
        }
        const { data } = await createToteamUser({ variables: { firstname, lastname, emailAddress, role } });
        if (data) {
            const {
                createToteamUserResolver: { success, error, user },
            } = data;
            if (success && user) {
                toast.success(`L'utilisateur ${firstname} ${lastname} a bien été créé ou mis à jour`);
                if (role === InternalUserRole.Toteam) {
                    navigate(`/user/${user._id}`);
                } else {
                    navigate(PAGES.userCreatedTotops.url);
                }
            } else {
                if (error) {
                    toast.error(error);
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        }
    });

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.userCreateToteam} />
                        </HeaderTitle>
                        <Link to="/users">
                            <TotemPrimaryButton $isSecondaryStyle requiredPermissions={[TotadminPermission.UsersRead]}>
                                Retour
                            </TotemPrimaryButton>
                        </Link>
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <DetailFormSelect
                                label="Rôle"
                                name="role"
                                options={ROLE_OPTIONS}
                                defaultValue={InternalUserRole.Toteam}
                                required
                            />
                            <DetailFormValue
                                label="Prénom"
                                data-test="user-create_firstname"
                                {...register('firstname', { required: true })}
                            />
                            <DetailFormValue
                                label="Nom"
                                data-test="user-create_lastname"
                                {...register('lastname', { required: true })}
                            />
                            <DetailFormValue
                                label="Adresse mail"
                                data-test="user-create_email"
                                {...register('emailAddress', { required: true })}
                            />
                            <ButtonContainer>
                                <TotemPrimaryButton
                                    $minWidth="140px"
                                    type="submit"
                                    disabled={!isDirty}
                                    data-test="user-create_submit"
                                >
                                    {loading ? (
                                        <Loader size="18px" mode={LoaderModeType.Spin} />
                                    ) : (
                                        <>Créer utilisateur</>
                                    )}
                                </TotemPrimaryButton>
                            </ButtonContainer>
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;
const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 15px;
    background-color: ${({ theme }) => theme.backgroundColor};
`;
